import * as React from 'react';

import { asSpelled } from '@/design';
import { oversightApi } from '../api/oversight-api';
import type { ICatalogCycle, ICatalogSurvey } from '../api/oversight-contracts';
import type {
  PeopleAccount,
  PeopleDisclosures,
  PeopleRegistry,
  PeopleTeamRouting,
  PeopleTeamRoutingSource
} from '../api/participant-contracts';
import { OUTCOME_WORDS, OUTCOMES } from '../pages/person-hub/team';

/**
 * The filters People, Teams and Firms share (SR-414; the plan's D13) — one spelling of each
 * vocabulary for three pages, since survey-api reads Teams' and Firms' filters with People's
 * words, over the record itself rather than over a person's affiliations. Every value is
 * survey-api's; a label is this side's words for it. Nothing here judges a value: a URL's
 * spelling no option has gets a position of its own (`withUnknownOption`) and is sent as spelled,
 * for survey-api's 400 `invalid-query` to say.
 */

export interface IFilterOption {
  value: string;
  label: string;
}

/**
 * A radio filter's position: a value survey-api has, or `null` — "Any", the position that narrows
 * nothing. `null` because no value the URL spells can be it: a string standing for "Any"
 * (`'__any__'`, once) is a spelling an address can carry, and `?verification=__any__` then checked
 * "Any" while the value was sent. `SelectChip` keeps the same line by prefixing its values.
 */
export interface IRadioOption {
  value: Nullable<string>;
  label: string;
}

export const VERIFICATIONS: readonly IRadioOption[] = [
  { value: null, label: 'Any' },
  { value: 'verified', label: 'A number verified' },
  { value: 'unverified', label: 'No number verified' },
  { value: 'denied', label: 'A number denied by its owner' }
];

/** survey-api's `registry` — the newest BrokerCheck answer across the row. */
export const REGISTRY_STATES: readonly { value: PeopleRegistry; label: string }[] = [
  { value: 'never_asked', label: 'Never asked' },
  { value: 'ok', label: 'ok' },
  { value: 'not_found', label: 'not found' },
  { value: 'error', label: 'error' }
];

/**
 * survey-api's `account` over a record (Teams, Firms), in `account-facts.tsx`' words. survey-api
 * reads every account linked — a point of contact (`contact`, SR-416) included — so "No account"
 * here is not the hub's, which counts the participant's own; the label says so, the filter is
 * survey-api's as sent.
 */
export const RECORD_ACCOUNT_STATES: readonly { value: PeopleAccount; label: string }[] = [
  { value: 'none', label: 'No account, nor a point of contact' },
  { value: 'not_linked', label: 'Not linked' },
  { value: 'never_signed_in', label: 'Never signed in' }
];

/**
 * The password states survey-api's `account` filter held until SR-476, and now refuses 400
 * `invalid-query`. An old link still spells them: `liveAccountFilter` drops one before the read,
 * so no list answers 400 and no chip names a filter nothing applies.
 */
const RETIRED_ACCOUNT_STATES: readonly string[] = ['no_password', 'change_pending', 'locked_out'];

/** The URL's `account`, less a retired value — what is read and drawn. Anything else is sent as
 *  spelled, for survey-api to judge. */
export function liveAccountFilter(value: Nullable<string>): Nullable<string> {
  return value !== null && RETIRED_ACCOUNT_STATES.includes(value) ? null : value;
}

/** Whether the URL names `name` as a filter the read applies — so an empty list does not say
 *  "with these filters" over a retired `account` value no chip draws. */
export function filterNamed(params: URLSearchParams, name: string): boolean {
  return name === 'account' ? liveAccountFilter(params.get(name)) !== null : params.has(name);
}

/** People's — plus `unregistered`, the roster rows alone (the owner's Q3), which Teams and Firms
 *  refuse: no record is a roster row. */
export const PEOPLE_ACCOUNT_STATES: readonly { value: PeopleAccount; label: string }[] = [
  ...RECORD_ACCOUNT_STATES,
  { value: 'unregistered', label: 'Not registered' }
];

/** survey-api's `disclosures`, over the CRD sets — what it tests, never a verdict on anyone:
 *  "clean" is its value, and a row can be both live and never checked. */
export const DISCLOSURE_STATES: readonly { value: PeopleDisclosures; label: string }[] = [
  { value: 'live', label: 'Live disclosures' },
  { value: 'clean', label: 'Checked, none live' },
  { value: 'unchecked', label: 'Never checked' }
];

/** survey-api's `teamRouting` — an outcome in the hub's words, or none yet. */
export const TEAM_ROUTINGS: readonly { value: PeopleTeamRouting; label: string }[] = [
  ...OUTCOMES.map((outcome) => ({ value: outcome, label: OUTCOME_WORDS[outcome] })),
  { value: 'unknown', label: 'Not routed yet' }
];

/** survey-api's `teamRoutingSource` — what the routing rests on; `none` a team nothing routed. */
export const TEAM_ROUTING_SOURCES: readonly { value: PeopleTeamRoutingSource; label: string }[] = [
  { value: 'firm', label: 'Firm' },
  { value: 'answers', label: 'Team’s answers' },
  { value: 'staff', label: 'Veritant' },
  { value: 'none', label: 'Nobody' }
];

/** survey-api's `teamLeadShared` — `true`, or absent: its `false` narrows nothing. */
export const LEAD_SHARED: readonly IRadioOption[] = [
  { value: null, label: 'Any' },
  { value: 'true', label: 'On another team' }
];

/** Teams' and Firms' `roster` (record search §3.4). */
export const ROSTER_STATES: readonly IFilterOption[] = [
  { value: 'unresolved', label: 'Someone not identified' },
  { value: 'empty', label: 'No member named' }
];

/** Firms' `secAdv` — whether one of the firm's numbers names a register firm. */
export const SEC_ADV_STATES: readonly IFilterOption[] = [
  { value: 'linked', label: 'Matched to the register' },
  { value: 'unlinked', label: 'Not matched' }
];

/** survey-api's matching rule for `q` over People, Teams and Firms (record search §2, D7, D8),
 *  said under each box. */
export const SEARCH_RULE =
  'Every word must be in one place — a name, an email, a team’s or firm’s name, a roster slot, a ' +
  'note. A word of one or two letters, or a number under seven digits, matches only where a word ' +
  'starts; CRD, SEC number and Salesforce id in any spelling; a pasted record id finds its record.';

/** The options, and the URL's value as a position of its own when it is not one of them — `''`
 *  as "Blank" (`asSpelled`). */
export function withUnknownOption(
  options: readonly IRadioOption[],
  value: Nullable<string>
): IRadioOption[] {
  return value === null || options.some((option) => option.value === value)
    ? [...options]
    : [...options, { value, label: asSpelled(value) }];
}

/** An option's label, or the URL's value as spelled (`asSpelled`), or null for none. */
export function optionLabel(
  options: readonly IRadioOption[],
  value: Nullable<string>
): Nullable<string> {
  if (value === null) return null;
  return options.find((option) => option.value === value)?.label ?? asSpelled(value);
}

/** A day of our own URL as the instant survey-api reads — that day's UTC midnight — and any
 *  other value as it is, for survey-api to judge. */
export function instantOf(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value;
}

/** `key` or `key:desc`; anything else is sent whole, as the key, for survey-api to refuse. */
export function sortOf<Key extends string>(value: string): { key: Key; descending: boolean } {
  const key = value.endsWith(':desc') ? value.slice(0, -':desc'.length) : value;
  return { key: key as Key, descending: key !== value };
}

/** The two catalogue reads the survey and cycle pickers are filled from, or `null` until both
 *  land — and for good if either fails: the list stands without them. */
export function useCatalogue(): Nullable<{ surveys: ICatalogSurvey[]; cycles: ICatalogCycle[] }> {
  const [catalogue, setCatalogue] =
    React.useState<Nullable<{ surveys: ICatalogSurvey[]; cycles: ICatalogCycle[] }>>(null);
  React.useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      oversightApi.surveys(controller.signal),
      oversightApi.cycles(undefined, controller.signal)
    ])
      .then(([surveys, cycles]) => {
        if (!controller.signal.aborted) setCatalogue({ surveys, cycles });
      })
      // The catalogue only fills the pickers: losing it leaves the list usable with no survey
      // or cycle to pick, rather than an error nobody can act on.
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  return catalogue;
}

/** The cycle codes the catalogue offers, narrowed to the surveys ticked (none ticked: all) —
 *  codes repeat across surveys. */
export function cycleCodesOf(
  catalogue: Nullable<{ cycles: ICatalogCycle[] }>,
  surveys: readonly string[]
): string[] {
  const relevant = (catalogue?.cycles ?? []).filter(
    (row) => surveys.length === 0 || surveys.includes(row.survey.code)
  );
  return [...new Set(relevant.map((row) => row.cycle.code))].sort();
}
